import React, { useRef, useState, useCallback, useMemo } from "react";
import {
  deletePerformerContextFile,
  uploadPerformerContextFiles,
} from "../businessModeService";
import { maybeRedirectOnSessionAuthError } from "../../core/services/sessionExpiry";
import { DEFAULT_ACTION_ITEM_CATEGORY_CATALOG } from "./actionItemCategories";
import SettingsInfoTip from "./shared/SettingsInfoTip";
import SettingsChipInput from "./shared/SettingsChipInput";
import CategoryDescriptionInput from "./shared/CategoryDescriptionInput";
import {
  formatPerformerGmailError,
  hasDriveContextLinks,
} from "./shared/performerContextLinks";
import { STEP_CHECKING_GMAIL, STEP_NEED_GMAIL, STEP_RUNNING } from "./usePerformerSetup";
import { renderUsageOrError } from "./shared/UsageLimitBanner";

/**
 * Contextual Drive connect hint shown only after the user adds Drive links.
 *
 * @example
 * <DriveContextHint driveInput="https://docs.google.com/document/d/abc" driveReady={false} onConnect={fn} />
 */
const DriveContextHint = ({
  driveInput,
  driveReady,
  onConnect,
  onDisconnect,
  compact = false,
  flat = false,
}) => {
  if (!hasDriveContextLinks(driveInput)) {
    return null;
  }
  if (driveReady) {
    if (flat) {
      return (
        <div className="settings-source-inline-hint">
          <span className="settings-row__hint">
            Google Drive connected — private links in your list can be read.
          </span>
          <div className="settings-sources-gmail__actions">
            <button type="button" className="settings-row__ghost-btn" onClick={onDisconnect}>
              Disconnect Drive
            </button>
          </div>
        </div>
      );
    }
    return compact ? (
      <div className="settings-sources-gmail settings-sources-gmail--hint">
        <span className="settings-row__hint">
          Google Drive connected — private links in your list can be read.
        </span>
        <div className="settings-sources-gmail__actions">
          <button type="button" className="settings-row__ghost-btn" onClick={onDisconnect}>
            Disconnect Drive
          </button>
        </div>
      </div>
    ) : (
      <div className="ai-drafts-panel__actions ai-drafts-panel__actions--row">
        <p className="ai-drafts-panel__status ai-drafts-panel__status--ok">
          Google Drive connected — private links can be read.
        </p>
        <button type="button" className="performer-overlay__secondary-btn" onClick={onDisconnect}>
          Disconnect Drive
        </button>
      </div>
    );
  }
  if (flat) {
    return (
      <div className="settings-source-inline-hint">
        <span className="settings-row__hint">
          These links may be private. Connect Google Drive to read files from your folders.
        </span>
        <div className="settings-sources-gmail__actions">
          <button type="button" className="settings-row__link-btn" onClick={onConnect}>
            Connect Drive →
          </button>
        </div>
      </div>
    );
  }
  return compact ? (
    <div className="settings-sources-gmail settings-sources-gmail--hint">
      <span className="settings-row__hint">
        These links may be private. Connect Google Drive to read files from your folders.
      </span>
      <div className="settings-sources-gmail__actions">
        <button type="button" className="settings-row__link-btn" onClick={onConnect}>
          Connect Drive →
        </button>
      </div>
    </div>
  ) : (
    <div className="ai-drafts-panel__actions ai-drafts-panel__actions--row">
      <p className="ai-drafts-panel__hint">
        These links may be private. Connect Google Drive to read files from your folders.
      </p>
      <button type="button" className="performer-overlay__secondary-btn" onClick={onConnect}>
        Connect Drive
      </button>
    </div>
  );
};

/**
 * Panel sections for Gmail, Drive context, and bulk draft runs (no modal shell).
 *
 * @param {boolean} compactSections - When true renders a flat labeled-row layout
 *   (Labels → Gmail → Context) suitable for settings pages; omits all h3 headings.
 *
 * @example
 * <PerformerSetupContent
 *   setup={usePerformerSetup({ pendingItems, ... })}
 *   pendingCount={12}
 *   compactSections
 * />
 */

/** `selectedLabel` value while the "+ Add label…" box is the one being edited. */
const NEW_LABEL_KEY = "__new__";

const PerformerSetupContent = ({
  setup,
  pendingCount,
  showRunControls = true,
  showCategorySection = true,
  compactCopy = false,
  compactContext = false,
  compactSections = false,
  sectionsLayout = "panel",
  activePiece = "",
  advancedToggle = null,
  categoryCatalog,
  onCategoryCatalogChange,
  categoryDescriptions,
  onCategoryDescriptionsChange,
  onContextEdited,
  onDriveInputBlur,
}) => {
  const {
    step,
    error,
    driveInput,
    setDriveInput,
    contextFiles,
    setContextFiles,
    driveStepNotice,
    setDriveStepNotice,
    runLaunchMode,
    gmailReady,
    gmailConnectLoading,
    handleGmailConnect,
    handleGmailDisconnect,
    handleDriveConnect,
    handleDriveDisconnect,
    calendarReady,
    calendarConnectLoading,
    handleCalendarConnect,
    handleCalendarDisconnect,
    slackReady,
    slackConnectLoading,
    handleSlackConnect,
    handleSlackDisconnect,
    driveReady,
    skipGmailToDrive,
    handleRunPerformer,
    unprocessedCount,
    runningCount,
  } = setup;

  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileActionNotice, setFileActionNotice] = useState(null);
  const [newCategoryInput, setNewCategoryInput] = useState("");
  // Label whose description is open for editing (NEW_LABEL_KEY = the label being typed).
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [descriptionDraft, setDescriptionDraft] = useState("");
  const [newCategoryDescription, setNewCategoryDescription] = useState("");

  const renderGmailConnectButton = () => (
    <button
      type="button"
      className="settings-source-connect-btn"
      onClick={handleGmailConnect}
      disabled={gmailConnectLoading}
      aria-busy={gmailConnectLoading}
    >
      {gmailConnectLoading ? (
        <span className="business-inline-spinner" role="status" aria-live="polite">
          <span className="business-inline-spinner__icon" aria-hidden="true" />
          Connecting to Gmail…
        </span>
      ) : (
        "Connect with Gmail"
      )}
    </button>
  );
  const isBusy = Boolean(runLaunchMode) || step === STEP_RUNNING;
  const uploadDisabled = isBusy || isUploading;

  const effectiveCatalog = Array.isArray(categoryCatalog)
    ? categoryCatalog
    : DEFAULT_ACTION_ITEM_CATEGORY_CATALOG;

  const savedDescriptions = useMemo(
    () =>
      categoryDescriptions && typeof categoryDescriptions === "object"
        ? categoryDescriptions
        : {},
    [categoryDescriptions]
  );
  const canDescribe = typeof onCategoryDescriptionsChange === "function";

  const handleAddCategory = useCallback(() => {
    const label = newCategoryInput.trim();
    if (!label) return;
    if (effectiveCatalog.some((c) => c.toLowerCase() === label.toLowerCase())) {
      setNewCategoryInput("");
      setNewCategoryDescription("");
      return;
    }
    const description = newCategoryDescription.replace(/\s+/g, " ").trim();
    onCategoryCatalogChange?.(
      [...effectiveCatalog, label],
      canDescribe && description ? { ...savedDescriptions, [label]: description } : undefined
    );
    setNewCategoryInput("");
    setNewCategoryDescription("");
    setSelectedLabel(null);
  }, [
    canDescribe,
    effectiveCatalog,
    newCategoryDescription,
    newCategoryInput,
    onCategoryCatalogChange,
    savedDescriptions,
  ]);

  const handleRemoveCategory = useCallback((index) => {
    const next = effectiveCatalog.filter((_, i) => i !== index);
    if (effectiveCatalog[index] === selectedLabel) setSelectedLabel(null);
    onCategoryCatalogChange?.(
      next.length > 0 ? next : DEFAULT_ACTION_ITEM_CATEGORY_CATALOG
    );
  }, [effectiveCatalog, onCategoryCatalogChange, selectedLabel]);

  const handleSelectLabel = (label) => {
    if (!canDescribe) return;
    if (selectedLabel === label) {
      setSelectedLabel(null);
      return;
    }
    setSelectedLabel(label);
    setDescriptionDraft(String(savedDescriptions[label] || ""));
  };

  const commitDescription = () => {
    if (!selectedLabel || selectedLabel === NEW_LABEL_KEY) return;
    const text = descriptionDraft.replace(/\s+/g, " ").trim();
    if (text === String(savedDescriptions[selectedLabel] || "").trim()) return;
    const next = { ...savedDescriptions };
    if (text) next[selectedLabel] = text;
    else delete next[selectedLabel];
    onCategoryDescriptionsChange(next);
  };

  /** Label chips (click one to edit its description) + the single open description input. */
  const renderLabelChipsEditor = () => {
    const editingNew = selectedLabel === NEW_LABEL_KEY && newCategoryInput.trim();
    const editingExisting =
      selectedLabel && selectedLabel !== NEW_LABEL_KEY && effectiveCatalog.includes(selectedLabel);
    return (
      <>
        <div className="settings-row__chips">
          {effectiveCatalog.map((label, index) => (
            <span
              key={label}
              className={`settings-chip${
                selectedLabel === label ? " settings-chip--selected" : ""
              }${savedDescriptions[label] ? " settings-chip--described" : ""}`}
            >
              <button
                type="button"
                className="settings-chip__text settings-chip__text-btn"
                onClick={() => handleSelectLabel(label)}
                disabled={!canDescribe}
                aria-expanded={selectedLabel === label}
                title={
                  canDescribe
                    ? savedDescriptions[label] || "Click to add a description"
                    : undefined
                }
              >
                {label}
              </button>
              <button
                type="button"
                className="settings-chip__remove"
                onClick={() => handleRemoveCategory(index)}
                disabled={effectiveCatalog.length <= 1}
                aria-label={`Remove ${label}`}
              >
                ×
              </button>
            </span>
          ))}
          <SettingsChipInput
            placeholder="+ Add label…"
            value={newCategoryInput}
            onChange={(e) => setNewCategoryInput(e.target.value)}
            onFocus={() => canDescribe && setSelectedLabel(NEW_LABEL_KEY)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); handleAddCategory(); }
            }}
            maxLength={40}
          />
        </div>
        {canDescribe && editingExisting ? (
          <CategoryDescriptionInput
            key={selectedLabel}
            label={selectedLabel}
            value={descriptionDraft}
            onChange={setDescriptionDraft}
            onCommit={commitDescription}
            autoFocus
            disabled={isBusy}
          />
        ) : null}
        {canDescribe && editingNew ? (
          <CategoryDescriptionInput
            label={newCategoryInput.trim()}
            value={newCategoryDescription}
            onChange={setNewCategoryDescription}
            onEnter={handleAddCategory}
            disabled={isBusy}
          />
        ) : null}
      </>
    );
  };

  const formatFileSize = (bytes) => {
    const size = Number(bytes) || 0;
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleUploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (files.length === 0 || uploadDisabled) return;
    setFileActionNotice(null);
    setIsUploading(true);
    try {
      const data = await uploadPerformerContextFiles(files);
      setContextFiles(Array.isArray(data?.context_files) ? data.context_files : []);
      const label =
        files.length === 1
          ? `"${files[0].name}" uploaded`
          : `${files.length} files uploaded`;
      setFileActionNotice({ type: "success", message: label });
    } catch (err) {
      const message = String(err?.message || "Failed to upload files");
      if (maybeRedirectOnSessionAuthError(message)) {
        return;
      }
      setFileActionNotice({
        type: "error",
        message,
      });
    } finally {
      setIsUploading(false);
      setIsDragOver(false);
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (uploadDisabled || deletingFileId) return;
    setFileActionNotice(null);
    setDeletingFileId(fileId);
    try {
      const data = await deletePerformerContextFile(fileId);
      setContextFiles(Array.isArray(data?.context_files) ? data.context_files : []);
      setFileActionNotice({ type: "success", message: "File removed" });
    } catch (err) {
      const message = String(err?.message || "Failed to delete file");
      if (maybeRedirectOnSessionAuthError(message)) {
        return;
      }
      setFileActionNotice({
        type: "error",
        message,
      });
    } finally {
      setDeletingFileId(null);
    }
  };

  const fileInputEl = (
    <input
      ref={fileInputRef}
      type="file"
      className="ai-drafts-panel__file-input"
      multiple
      accept=".pdf,.docx,.txt,.md,.xlsx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,text/markdown"
      disabled={uploadDisabled}
      onChange={(e) => {
        void handleUploadFiles(e.target.files);
        e.target.value = "";
      }}
    />
  );

  const renderTaskLabelsContent = (className = "") => (
    <div className={className} aria-label="Task labels">
      {className.includes("settings-general-piece") ? (
        <p className="settings-general-piece__desc">Press Enter to add a label.</p>
      ) : null}
      {renderLabelChipsEditor()}
    </div>
  );

  const renderGmailContent = (layoutVariant = "compact") => {
    const isFlatBlock = layoutVariant === "stacked" || layoutVariant === "piece";
    const showTitle = layoutVariant === "stacked";
    const wrapperClass = isFlatBlock ? "settings-source-block" : "settings-sources-gmail";

    const statusBadge =
      step === STEP_CHECKING_GMAIL ? (
        <span className="settings-row__hint">Checking…</span>
      ) : gmailReady ? (
        <span className="settings-row__badge settings-row__badge--ok">Connected</span>
      ) : (
        <span className="settings-row__badge settings-row__badge--warn">Not connected</span>
      );

    const body =
      step === STEP_CHECKING_GMAIL ? null : gmailReady ? (
        <>
          <span className="settings-row__hint">Can save drafts in your mailbox</span>
          <div className="settings-sources-gmail__actions">
            <button
              type="button"
              className="settings-row__ghost-btn"
              onClick={() => {
                if (
                  window.confirm(
                    "Disconnect Gmail? Drafts will no longer save to your mailbox."
                  )
                ) {
                  void handleGmailDisconnect();
                }
              }}
            >
              Disconnect
            </button>
          </div>
        </>
      ) : (
        <>
          <span className="settings-row__hint">
            {error && step === STEP_NEED_GMAIL
              ? formatPerformerGmailError(error)
              : "Save AI-generated drafts directly in your mailbox."}
          </span>
          <div className="settings-sources-gmail__actions">
            {renderGmailConnectButton()}
          </div>
        </>
      );

    return (
      <div className={wrapperClass}>
        {showTitle ? (
          <div className="settings-source-block__heading">
            <h4 className="settings-source-block__title">Gmail drafts</h4>
            {statusBadge}
          </div>
        ) : layoutVariant === "compact" ? (
          <div className="settings-sources-gmail__header">
            <span className="settings-sources-gmail__name">Gmail drafts</span>
            {statusBadge}
          </div>
        ) : (
          <div className="settings-source-block__heading">{statusBadge}</div>
        )}
        {body}
      </div>
    );
  };

  const renderCalendarContent = (layoutVariant = "compact") => {
    const isFlatBlock = layoutVariant === "stacked" || layoutVariant === "piece";
    const showTitle = layoutVariant === "stacked";
    const wrapperClass = isFlatBlock ? "settings-source-block" : "settings-sources-gmail";

    const statusBadge = calendarReady ? (
      <span className="settings-row__badge settings-row__badge--ok">Connected</span>
    ) : (
      <span className="settings-row__badge settings-row__badge--warn">Not connected</span>
    );

    const body = calendarReady ? (
      <>
        <span className="settings-row__hint">
          Suggests open times from your calendar. Prefills only — never auto-books.
        </span>
        <div className="settings-sources-gmail__actions">
          <button
            type="button"
            className="settings-row__ghost-btn"
            onClick={() => {
              if (
                window.confirm(
                  "Disconnect Calendar? Meeting suggestions will use default hours instead of FreeBusy."
                )
              ) {
                void handleCalendarDisconnect();
              }
            }}
          >
            Disconnect
          </button>
        </div>
      </>
    ) : (
      <>
        <span className="settings-row__hint">
          Optional. Without Calendar, AI suggests times from your meeting preferences.
        </span>
        <div className="settings-sources-gmail__actions">
          <button
            type="button"
            className="settings-source-connect-btn"
            onClick={() => void handleCalendarConnect()}
            disabled={calendarConnectLoading}
            aria-busy={calendarConnectLoading}
          >
            {calendarConnectLoading ? (
              <span className="business-inline-spinner" role="status" aria-live="polite">
                <span className="business-inline-spinner__icon" aria-hidden="true" />
                Connecting to Calendar…
              </span>
            ) : (
              "Connect Google Calendar"
            )}
          </button>
        </div>
      </>
    );

    return (
      <div className={wrapperClass}>
        {showTitle ? (
          <div className="settings-source-block__heading">
            <h4 className="settings-source-block__title">Google Calendar</h4>
            {statusBadge}
          </div>
        ) : layoutVariant === "compact" ? (
          <div className="settings-sources-gmail__header">
            <span className="settings-sources-gmail__name">Google Calendar</span>
            {statusBadge}
          </div>
        ) : (
          <div className="settings-source-block__heading">{statusBadge}</div>
        )}
        {body}
      </div>
    );
  };

  const renderSlackContent = (layoutVariant = "compact") => {
    const isFlatBlock = layoutVariant === "stacked" || layoutVariant === "piece";
    const showTitle = layoutVariant === "stacked";
    const wrapperClass = isFlatBlock ? "settings-source-block" : "settings-sources-gmail";

    const statusBadge = slackReady ? (
      <span className="settings-row__badge settings-row__badge--ok">Connected</span>
    ) : (
      <span className="settings-row__badge settings-row__badge--warn">Not connected</span>
    );

    const body = slackReady ? (
      <>
        <span className="settings-row__hint">
          Lets AI Assist search your channels/DMs and send a message once you pick one.
        </span>
        <div className="settings-sources-gmail__actions">
          <button
            type="button"
            className="settings-row__ghost-btn"
            onClick={() => {
              if (
                window.confirm(
                  "Disconnect Slack? “Send via Slack” will no longer be available until you reconnect."
                )
              ) {
                void handleSlackDisconnect();
              }
            }}
          >
            Disconnect
          </button>
        </div>
      </>
    ) : (
      <>
        <span className="settings-row__hint">
          Optional. Connect to send drafted messages via Slack — search-and-send only, your
          messages are never read.
        </span>
        <div className="settings-sources-gmail__actions">
          <button
            type="button"
            className="settings-source-connect-btn"
            onClick={() => void handleSlackConnect()}
            disabled={slackConnectLoading}
            aria-busy={slackConnectLoading}
          >
            {slackConnectLoading ? (
              <span className="business-inline-spinner" role="status" aria-live="polite">
                <span className="business-inline-spinner__icon" aria-hidden="true" />
                Connecting to Slack…
              </span>
            ) : (
              "Connect with Slack"
            )}
          </button>
        </div>
      </>
    );

    return (
      <div className={wrapperClass}>
        {showTitle ? (
          <div className="settings-source-block__heading">
            <h4 className="settings-source-block__title">Slack</h4>
            {statusBadge}
          </div>
        ) : layoutVariant === "compact" ? (
          <div className="settings-sources-gmail__header">
            <span className="settings-sources-gmail__name">Slack</span>
            {statusBadge}
          </div>
        ) : (
          <div className="settings-source-block__heading">{statusBadge}</div>
        )}
        {body}
      </div>
    );
  };

  const renderReferenceContent = (layoutVariant = "compact") => {
    const isFlatBlock = layoutVariant === "stacked" || layoutVariant === "piece";
    const showSubtitle = layoutVariant === "stacked";

    const body = (
      <>
        {showSubtitle ? (
          <p className="settings-source-block__desc">
            Drive links or uploaded files the AI can reference when writing.
          </p>
        ) : null}
        <textarea
          className="settings-compact-textarea"
          placeholder={"Paste Google Drive links, one per line…\nhttps://docs.google.com/document/d/…"}
          value={driveInput}
          onChange={(e) => {
            setDriveInput(e.target.value);
            setDriveStepNotice("");
            onContextEdited?.();
          }}
          onBlur={() => onDriveInputBlur?.()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const dropped = e.dataTransfer.getData("text") || e.dataTransfer.getData("text/plain");
            const urls = String(dropped || "")
              .split(/\s+/)
              .map((line) => line.trim())
              .filter((line) => line.startsWith("http"));
            if (urls.length === 0) return;
            const merged = [driveInput.trim(), ...urls].filter(Boolean).join("\n");
            setDriveInput(merged);
            setDriveStepNotice("");
            onContextEdited?.();
            onDriveInputBlur?.();
          }}
          rows={layoutVariant === "piece" ? 3 : 4}
          disabled={Boolean(runLaunchMode) || step === STEP_RUNNING}
        />
        <DriveContextHint
          compact
          flat={isFlatBlock}
          driveInput={driveInput}
          driveReady={driveReady}
          onConnect={() => void handleDriveConnect()}
          onDisconnect={() => {
            if (
              window.confirm(
                "Disconnect Drive? Private Drive links will no longer be readable."
              )
            ) {
              void handleDriveDisconnect();
            }
          }}
        />
        <div className="settings-compact-files">
          {fileInputEl}
          <button
            type="button"
            className="settings-compact-attach-btn"
            disabled={uploadDisabled}
            onClick={() => fileInputRef.current?.click()}
          >
            {isUploading ? "Uploading…" : "Add files"}
          </button>
          {contextFiles.map((file) => {
            const isDeleting = deletingFileId === file.id;
            return (
              <span
                key={file.id}
                className={`settings-chip${isDeleting ? " settings-chip--busy" : ""}`}
              >
                <span
                  className="settings-chip__text"
                  title={`${file.filename} (${formatFileSize(file.size_bytes)})`}
                >
                  {file.filename}
                </span>
                <button
                  type="button"
                  className="settings-chip__remove"
                  disabled={uploadDisabled || Boolean(deletingFileId)}
                  onClick={() => void handleDeleteFile(file.id)}
                  aria-label={isDeleting ? `Removing ${file.filename}` : `Remove ${file.filename}`}
                >
                  ×
                </button>
              </span>
            );
          })}
          {fileActionNotice ? (
            <span
              className={
                fileActionNotice.type === "success"
                  ? "settings-file-notice settings-file-notice--ok"
                  : "settings-file-notice settings-file-notice--error"
              }
              role={fileActionNotice.type === "error" ? "alert" : "status"}
            >
              {fileActionNotice.message}
            </span>
          ) : null}
        </div>
      </>
    );

    if (!isFlatBlock) {
      return body;
    }

    if (showSubtitle) {
      return (
        <div className="settings-source-block">
          <h4 className="settings-source-block__title">Reference documents</h4>
          {body}
        </div>
      );
    }

    return <div className="settings-source-block">{body}</div>;
  };

  if (sectionsLayout === "piece") {
    if (activePiece === "task-labels") {
      return renderTaskLabelsContent("settings-general-piece");
    }
    if (activePiece === "gmail-drafts") {
      return <div className="settings-general-piece">{renderGmailContent("piece")}</div>;
    }
    if (activePiece === "calendar") {
      return <div className="settings-general-piece">{renderCalendarContent("piece")}</div>;
    }
    if (activePiece === "slack-send") {
      return <div className="settings-general-piece">{renderSlackContent("piece")}</div>;
    }
    if (activePiece === "reference-docs") {
      return (
        <div className="settings-general-piece">
          <p className="settings-general-piece__desc">
            Optional Drive links and files the AI can read when drafting.
          </p>
          {renderReferenceContent("piece")}
        </div>
      );
    }
    return null;
  }

  if (sectionsLayout === "stacked-sources") {
    return (
      <div className="settings-source-stack">
        {renderGmailContent("stacked")}
        {renderCalendarContent("stacked")}
        {renderSlackContent("stacked")}
        {renderReferenceContent("stacked")}
      </div>
    );
  }

  if (compactSections) {
    return (
      <>
        {showCategorySection ? (
          <div className="settings-row settings-row--labels" aria-label="Task labels">
            <span className="settings-row__label">
              <span className="settings-row__label-text">Labels</span>
              <SettingsInfoTip
                label="About task labels"
                text="Labels AI can assign to your tasks when categorizing them."
              />
            </span>
            <div className="settings-row__labels-editor">{renderLabelChipsEditor()}</div>
          </div>
        ) : null}

        <div className="settings-row settings-row--sources">
          <span className="settings-row__label">
            <span className="settings-row__label-text">Apps &amp; docs</span>
            <SettingsInfoTip
              label="About AI Actions Assist"
              text="Connect Gmail here to save AI drafts in your mailbox. Add Drive links below only if you want the AI to read those documents. Connectors use separate read-only Gmail access."
            />
          </span>
          <div className="settings-row__content">
            <div className="settings-sources-gmail">
              <div className="settings-sources-gmail__header">
                <span className="settings-sources-gmail__name">Gmail drafts</span>
                {step === STEP_CHECKING_GMAIL ? (
                  <span className="settings-row__hint">Checking…</span>
                ) : gmailReady ? (
                  <span className="settings-row__badge settings-row__badge--ok">Connected</span>
                ) : null}
              </div>
              {step === STEP_CHECKING_GMAIL ? null : gmailReady ? (
                <>
                  <span className="settings-row__hint">Can save drafts in your mailbox</span>
                  <div className="settings-sources-gmail__actions">
                    <button
                      type="button"
                      className="settings-row__ghost-btn"
                      onClick={() => {
                        if (
                          window.confirm(
                            "Disconnect Gmail? Drafts will no longer save to your mailbox."
                          )
                        ) {
                          void handleGmailDisconnect();
                        }
                      }}
                    >
                      Disconnect
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <span className="settings-row__hint">
                    {error && step === STEP_NEED_GMAIL
                      ? formatPerformerGmailError(error)
                      : "Save AI drafts in your mailbox"}
                  </span>
                  <div className="settings-sources-gmail__actions">
                    {renderGmailConnectButton()}
                  </div>
                </>
              )}
            </div>
            <textarea
              className="settings-compact-textarea"
              placeholder={"Paste Google Drive links, one per line…\nhttps://docs.google.com/document/d/…"}
              value={driveInput}
              onChange={(e) => {
                setDriveInput(e.target.value);
                setDriveStepNotice("");
                onContextEdited?.();
              }}
              onBlur={() => onDriveInputBlur?.()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const dropped = e.dataTransfer.getData("text") || e.dataTransfer.getData("text/plain");
                const urls = String(dropped || "")
                  .split(/\s+/)
                  .map((line) => line.trim())
                  .filter((line) => line.startsWith("http"));
                if (urls.length === 0) return;
                const merged = [driveInput.trim(), ...urls].filter(Boolean).join("\n");
                setDriveInput(merged);
                setDriveStepNotice("");
                onContextEdited?.();
                onDriveInputBlur?.();
              }}
              rows={4}
              disabled={Boolean(runLaunchMode) || step === STEP_RUNNING}
            />
            <DriveContextHint
              compact
              driveInput={driveInput}
              driveReady={driveReady}
              onConnect={() => void handleDriveConnect()}
              onDisconnect={() => {
                if (
                  window.confirm(
                    "Disconnect Drive? Private Drive links will no longer be readable."
                  )
                ) {
                  void handleDriveDisconnect();
                }
              }}
            />
            <div className="settings-compact-files">
              {fileInputEl}
              <button
                type="button"
                className="settings-compact-attach-btn"
                disabled={uploadDisabled}
                onClick={() => fileInputRef.current?.click()}
              >
                {isUploading ? "Uploading…" : "Add files"}
              </button>
              {contextFiles.map((file) => {
                const isDeleting = deletingFileId === file.id;
                return (
                  <span
                    key={file.id}
                    className={`settings-chip${isDeleting ? " settings-chip--busy" : ""}`}
                  >
                    <span
                      className="settings-chip__text"
                      title={`${file.filename} (${formatFileSize(file.size_bytes)})`}
                    >
                      {file.filename}
                    </span>
                    <button
                      type="button"
                      className="settings-chip__remove"
                      disabled={uploadDisabled || Boolean(deletingFileId)}
                      onClick={() => void handleDeleteFile(file.id)}
                      aria-label={isDeleting ? `Removing ${file.filename}` : `Remove ${file.filename}`}
                    >
                      ×
                    </button>
                  </span>
                );
              })}
              {fileActionNotice ? (
                <span
                  className={
                    fileActionNotice.type === "success"
                      ? "settings-file-notice settings-file-notice--ok"
                      : "settings-file-notice settings-file-notice--error"
                  }
                  role={fileActionNotice.type === "error" ? "alert" : "status"}
                >
                  {fileActionNotice.message}
                </span>
              ) : null}
            </div>
          </div>
        </div>

        {advancedToggle}
      </>
    );
  }

  return (
    <>
      <section className="ai-drafts-panel__section" aria-labelledby="ai-drafts-gmail-heading">
        <h3 id="ai-drafts-gmail-heading" className="ai-drafts-panel__section-title">
          Gmail drafts
        </h3>
        {step === STEP_CHECKING_GMAIL ? (
          <div className="business-inline-spinner">
            <span className="business-inline-spinner__icon" aria-hidden="true" />
            <span>Checking Gmail permissions…</span>
          </div>
        ) : gmailReady ? (
          <div className="ai-drafts-panel__actions ai-drafts-panel__actions--row">
            <p className="ai-drafts-panel__status ai-drafts-panel__status--ok">
              Gmail connected — drafts can be saved to your mailbox.
            </p>
            <button
              type="button"
              className="performer-overlay__secondary-btn"
              onClick={() => {
                if (
                  window.confirm(
                    "Disconnect Gmail? Drafts will no longer save to your mailbox."
                  )
                ) {
                  void handleGmailDisconnect();
                }
              }}
            >
              Disconnect
            </button>
          </div>
        ) : (
          <>
            <p className="ai-drafts-panel__hint">
              {compactCopy
                ? "Connect Gmail to save drafts in your mailbox."
                : "Connect Gmail so the AI can read threads and save drafts. Nothing is sent automatically."}
            </p>
            {error && step === STEP_NEED_GMAIL ? (
              <p className="ai-drafts-panel__notice">{formatPerformerGmailError(error)}</p>
            ) : null}
            <div className="ai-drafts-panel__actions ai-drafts-panel__actions--row">
              {renderGmailConnectButton()}
              <button
                type="button"
                className="performer-overlay__secondary-btn"
                onClick={skipGmailToDrive}
              >
                Skip — draft without email history
              </button>
            </div>
          </>
        )}
      </section>

      <section
        className="ai-drafts-panel__section ai-drafts-panel__section--context"
        aria-labelledby="ai-drafts-context-heading"
      >
        <h3 id="ai-drafts-context-heading" className="ai-drafts-panel__section-title">
          Context documents
        </h3>
        <p className="ai-drafts-panel__hint">
          {compactCopy
            ? "Upload files or paste Google Drive links to improve suggestions."
            : "Upload your reference documents or paste Google Drive links below. The AI will read them to give you better, more relevant suggestions."}
        </p>
        <textarea
          className="performer-overlay__textarea ai-drafts-panel__textarea ai-drafts-panel__textarea--drive"
          placeholder={"https://docs.google.com/document/d/...\nhttps://docs.google.com/spreadsheets/d/..."}
          value={driveInput}
          onChange={(e) => {
            setDriveInput(e.target.value);
            setDriveStepNotice("");
            onContextEdited?.();
          }}
          onBlur={() => onDriveInputBlur?.()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const dropped = e.dataTransfer.getData("text") || e.dataTransfer.getData("text/plain");
            const urls = String(dropped || "")
              .split(/\s+/)
              .map((line) => line.trim())
              .filter((line) => line.startsWith("http"));
            if (urls.length === 0) return;
            const merged = [driveInput.trim(), ...urls].filter(Boolean).join("\n");
            setDriveInput(merged);
            setDriveStepNotice("");
            onContextEdited?.();
            onDriveInputBlur?.();
          }}
          rows={compactContext ? 3 : 5}
          disabled={Boolean(runLaunchMode) || step === STEP_RUNNING}
        />
        <DriveContextHint
          driveInput={driveInput}
          driveReady={driveReady}
          onConnect={() => void handleDriveConnect()}
          onDisconnect={() => {
            if (
              window.confirm(
                "Disconnect Drive? Private Drive links will no longer be readable."
              )
            ) {
              void handleDriveDisconnect();
            }
          }}
        />
        <div
          className={[
            "ai-drafts-panel__upload-zone",
            uploadDisabled ? "ai-drafts-panel__upload-zone--disabled" : "",
            isDragOver && !uploadDisabled ? "ai-drafts-panel__upload-zone--dragover" : "",
            isUploading ? "ai-drafts-panel__upload-zone--uploading" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          aria-busy={isUploading}
          onDragEnter={(e) => {
            e.preventDefault();
            if (!uploadDisabled) setIsDragOver(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            if (e.currentTarget.contains(e.relatedTarget)) return;
            setIsDragOver(false);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            if (!uploadDisabled) setIsDragOver(true);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragOver(false);
            if (uploadDisabled) return;
            void handleUploadFiles(e.dataTransfer.files);
          }}
        >
          {fileInputEl}
          {isUploading ? (
            <div className="business-inline-spinner ai-drafts-panel__upload-status" role="status">
              <span className="business-inline-spinner__icon" aria-hidden="true" />
              <span>Uploading…</span>
            </div>
          ) : (
            <button
              type="button"
              className="performer-overlay__secondary-btn"
              disabled={uploadDisabled}
              onClick={() => fileInputRef.current?.click()}
            >
              Choose files
            </button>
          )}
          <span className="ai-drafts-panel__helper">
            {isUploading
              ? "Please wait while your files are processed."
              : compactContext
                ? "PDF, DOCX, TXT, MD, XLSX — max 10 MB each"
                : "Select or drag your reference documents (max 10 MB each, 50 MB total)"}
          </span>
        </div>
        {fileActionNotice ? (
          <p
            className={
              fileActionNotice.type === "success"
                ? "ai-drafts-panel__status ai-drafts-panel__status--ok ai-drafts-panel__file-notice"
                : "ai-drafts-panel__notice ai-drafts-panel__file-notice"
            }
            role={fileActionNotice.type === "error" ? "alert" : "status"}
          >
            {fileActionNotice.message}
          </p>
        ) : null}
        {contextFiles.length > 0 ? (
          <ul className="ai-drafts-panel__catalog-list" aria-label="Uploaded context files">
            {contextFiles.map((file) => {
              const isDeleting = deletingFileId === file.id;
              const sizeLabel = formatFileSize(file.size_bytes);
              return (
                <li
                  key={file.id}
                  className={`ai-drafts-panel__catalog-item ai-drafts-panel__catalog-item--file${
                    isDeleting ? " ai-drafts-panel__catalog-item--busy" : ""
                  }`}
                >
                  <span
                    className="ai-drafts-panel__catalog-label"
                    title={`${file.filename} (${sizeLabel})`}
                  >
                    {file.filename}
                  </span>
                  <button
                    type="button"
                    className="ai-drafts-panel__catalog-remove"
                    disabled={uploadDisabled || Boolean(deletingFileId)}
                    onClick={() => void handleDeleteFile(file.id)}
                    aria-label={isDeleting ? `Removing ${file.filename}` : `Remove ${file.filename}`}
                  >
                    ×
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </section>

      {advancedToggle}

      {showCategorySection ? (
        <section
          className="ai-drafts-panel__section ai-drafts-panel__section--categories"
          aria-labelledby="ai-drafts-categories-heading"
        >
          <h3 id="ai-drafts-categories-heading" className="ai-drafts-panel__section-title">
            Task labels
          </h3>
          <p className="ai-drafts-panel__hint">Choose labels AI can assign to tasks.</p>
          <ul className="ai-drafts-panel__catalog-list" aria-label="Task categories">
            {effectiveCatalog.map((label, index) => (
              <li key={label} className="ai-drafts-panel__catalog-item">
                <span className="ai-drafts-panel__catalog-label">{label}</span>
                <button
                  type="button"
                  className="ai-drafts-panel__catalog-remove"
                  onClick={() => handleRemoveCategory(index)}
                  aria-label={`Remove ${label}`}
                  disabled={effectiveCatalog.length <= 1}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
          <div className="ai-drafts-panel__catalog-add">
            <input
              type="text"
              className="ai-drafts-panel__catalog-input"
              placeholder="Add a category…"
              value={newCategoryInput}
              onChange={(e) => setNewCategoryInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); handleAddCategory(); }
              }}
              maxLength={40}
            />
            <button
              type="button"
              className="performer-overlay__secondary-btn ai-drafts-panel__catalog-add-btn"
              onClick={handleAddCategory}
              disabled={!newCategoryInput.trim()}
            >
              Add
            </button>
          </div>
        </section>
      ) : null}

      {showRunControls ? (
        <section className="ai-drafts-panel__section" aria-labelledby="ai-drafts-run-heading">
          <h3 id="ai-drafts-run-heading" className="ai-drafts-panel__section-title">
            Run AI
          </h3>
          {step === STEP_RUNNING ? (
            <div className="performer-overlay__running ai-drafts-panel__running">
              <div className="performer-overlay__spinner">
                <div className="performer-overlay__spinner-ring" />
              </div>
              <p className="performer-overlay__running-detail">
                AI working on {runningCount} task{runningCount !== 1 ? "s" : ""}…
              </p>
              <p className="ai-drafts-panel__hint">Classifying tasks · Reading context · Generating output</p>
            </div>
          ) : (
            <>
              {error && step !== STEP_NEED_GMAIL
                ? renderUsageOrError(error, "p", "ai-drafts-panel__notice")
                : null}
              {driveStepNotice ? (
                <p className="ai-drafts-panel__notice">{driveStepNotice}</p>
              ) : null}
              {runLaunchMode ? (
                <div className="business-inline-spinner performer-overlay__launch-status" role="status">
                  <span className="business-inline-spinner__icon" aria-hidden="true" />
                  <span>
                    Preparing {runLaunchMode === "unprocessed" ? unprocessedCount : pendingCount} task
                    {(runLaunchMode === "unprocessed" ? unprocessedCount : pendingCount) !== 1
                      ? "s"
                      : ""}
                    …
                  </span>
                </div>
              ) : null}
              <div className="ai-drafts-panel__actions" aria-busy={Boolean(runLaunchMode)}>
                <button
                  type="button"
                  className="performer-overlay__primary-btn"
                  onClick={() => handleRunPerformer("all")}
                  disabled={pendingCount === 0 || Boolean(runLaunchMode)}
                  aria-busy={runLaunchMode === "all"}
                >
                  {runLaunchMode === "all" ? "Starting…" : `Run AI on all tasks (${pendingCount})`}
                </button>
                <button
                  type="button"
                  className="performer-overlay__primary-btn performer-overlay__primary-btn--secondary"
                  onClick={() => handleRunPerformer("unprocessed")}
                  disabled={unprocessedCount === 0 || Boolean(runLaunchMode)}
                  aria-busy={runLaunchMode === "unprocessed"}
                  title={
                    unprocessedCount === 0
                      ? "All open tasks already have AI results"
                      : `Skip ${pendingCount - unprocessedCount} task(s) that already have results`
                  }
                >
                  {runLaunchMode === "unprocessed"
                    ? "Starting…"
                    : `Run AI on remaining tasks (${unprocessedCount})`}
                </button>
              </div>
            </>
          )}
        </section>
      ) : null}
    </>
  );
};

export default PerformerSetupContent;
